/**
 * In-memory flag definitions for OpenFeature's LocalProvider.
 * This file contains the flag definitions for the LocalProvider.
 * It is used to define the flags that are available in the LocalProvider.
 * The flags are defined as an object with the flag key as the key and the flag definition as the value.
 * The flag definition is an object with the following properties:
 * - type: The type of the flag. This can be one of 'boolean', 'string', 'number', or 'object'.
 * - defaultValue: The default value of the flag. This is the value that will be returned if the flag is not found in the LocalProvider.
 * - description: A description of the flag. This is used to provide information about the flag to the user.
 * - values: An array of possible values for the flag. This is used to provide a list of possible values for the flag to the user.
 */

interface EvenSplitDefinition<T> {
  weightedSplit?: false
  values: T[]
}

interface WeightedSplitDefinition<T> {
  weightedSplit: true
  values: {
    value: T
    weight: number
  }[]
}

export type FeatureDefinition<T> = {
  type: 'boolean' | 'string' | 'number'
  defaultValue: T
  description: string
} & (EvenSplitDefinition<T> | WeightedSplitDefinition<T>)

export type FeaturePrimitive = boolean | string | number

export interface FeaturesDefinition {
  [key: string]: FeatureDefinition<FeaturePrimitive>
}

export const flags: FeaturesDefinition = {
  layoutSkeleton: {
    type: 'string',
    defaultValue: 'control',
    description: 'The layout skeleton to use',
    weightedSplit: true,
    values: [
      {
        value: 'control',
        weight: 80,
      },
      {
        value: 'test',
        weight: 20,
      },
    ],
  },
  siteSettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of site settings to use',
    values: ['control', 'test'],
  },
  layoutSettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of layout settings to use',
    values: ['control', 'test'],
  },
  adSettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of layout settings to use',
    values: ['control', 'test'],
  },
  summaryPageSettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of summary page settings to use',
    values: ['control', 'test'],
  },
  contentSource: {
    type: 'string',
    defaultValue: 'dynamodb',
    description: 'Which content source to use',
    values: ['content-api', 'dynamodb', 'discover-feed'],
  },
  showArticleImage: {
    type: 'boolean',
    defaultValue: false,
    description: 'Whether to show the article image',
    values: [true, false],
  },
  enableSearchboxUrlNavigation: {
    type: 'boolean',
    defaultValue: false,
    description:
      'Enable omnibox-style URL navigation in searchbox (A/B test for DXT-1619)',
    values: [true, false],
  },
}
